'use client';

import {
  CaseStudyBody,
  CaseStudyResultsTable,
} from '@/components/case-study/case-study-body';
import { CaseStudyHero } from '@/components/case-study/case-study-hero';
import { CustomerVideos } from '@/components/sections/customer-videos';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

const OTOCaseStudy = () => {
  const { t, isRTL } = useTranslation();
  return (
    <>
      <CaseStudyHero
        customerName="OTO"
        title={
          t.customerStories[
            'OTO is automating more than 77% of the support with +90% CSAT score'
          ]
        }
        subtitle={
          t.customerStories[
            "Open's AI agent handles customer inquiries fully autonomously. Customers get instant answers while the support team focuses on what actually needs human judgment."
          ]
        }
        stats={[
          {
            value: isRTL ? '٧٧٪' : '77%',
            label: t.customerStories['Automation rate'],
          },
          {
            value: isRTL ? '+٩٠٪' : '90%+',
            label: t.customerStories['CSAT score'],
          },
        ]}
        // The film is spoken in Arabic, so it only earns its place on /ar.
        // The English hero falls back to text + stats, as on the story
        // pages that never had footage.
        videoSrc={isRTL ? '/home/stories/oto-v2.mp4' : undefined}
        videoStartAt={2.5}
      />

      <CaseStudyBody
        aside={{
          logoAlt: 'OTO',
          customerName: 'OTO',
          company:
            t.customerStories[
              'OTO is a logistics technology platform connecting e-commerce businesses with 350+ carriers across Saudi Arabia, UAE, and Turkey.'
            ],
          industry: t.customerStories['Logistics / Technology'],
          websiteUrl: 'https://oto.com',
          extras: [
            {
              label: t.customerStories.Location,
              value: t.customerStories['Middle East'],
            },
          ],
          keyResults: [
            t.customerStories['• 77% automation rate'],
            t.customerStories['• 90%+ CSAT score'],
            t.customerStories['• AI-first operating model'],
          ],
        }}
      >
        <h2>{t.customerStories['About OTO']}</h2>
        <p>
          {
            t.customerStories[
              'OTO is a logistics technology platform that helps e-commerce businesses across Saudi Arabia, UAE, and Turkey manage their entire shipping operations. The platform connects merchants with over 350 carriers and 30+ sales channels, handling everything from order management and tracking to returns and analytics.'
            ]
          }
        </p>
        <p>
          {
            t.customerStories[
              'Founded in 2019 by Mohamed Razaz, OTO has grown rapidly and raised $8M in Series A funding in 2024. With thousands of merchants relying on the platform daily, support volume was becoming a challenge.'
            ]
          }
        </p>

        <h2>{t.customerStories['The Support Challenge']}</h2>
        <p>
          {
            t.customerStories[
              'When you power shipping for thousands of e-commerce businesses, support requests pile up fast. Merchants constantly need help with:'
            ]
          }
        </p>
        <ul>
          <li>
            <strong>{t.customerStories['Tracking inquiries']}</strong> –{' '}
            {
              t.customerStories[
                '"Where is my shipment?" is the most common question in logistics'
              ]
            }
          </li>
          <li>
            <strong>{t.customerStories['Shipping rate questions']}</strong> –{' '}
            {
              t.customerStories[
                'Merchants comparing rates across 350+ carriers'
              ]
            }
          </li>
          <li>
            <strong>{t.customerStories['Return and refund requests']}</strong> –{' '}
            {
              t.customerStories[
                'Managing reverse logistics across multiple carriers'
              ]
            }
          </li>
          <li>
            <strong>{t.customerStories['Integration troubleshooting']}</strong>{' '}
            – {t.customerStories['Connecting sales channels and carrier APIs']}
          </li>
          <li>
            <strong>
              {t.customerStories['Billing and invoice questions']}
            </strong>{' '}
            –{' '}
            {
              t.customerStories[
                'Understanding charges across different carriers'
              ]
            }
          </li>
        </ul>
        <p>
          {
            t.customerStories[
              "Most of these questions are repetitive but time-sensitive. A merchant waiting for shipping help can't fulfill orders. The support team was spending most of their time on routine inquiries instead of helping merchants with complex operational challenges."
            ]
          }
        </p>

        <h2>{t.customerStories['How Open Helps']}</h2>
        <p>
          {
            t.customerStories[
              "OTO deployed Open's AI agent to handle the high-volume, routine support requests. The AI connects directly to OTO's systems and can:"
            ]
          }
        </p>
        <ul>
          <li>
            {
              t.customerStories[
                'Pull real-time tracking information from any carrier'
              ]
            }
          </li>
          <li>
            {
              t.customerStories[
                'Compare shipping rates and recommend the best options'
              ]
            }
          </li>
          <li>
            {
              t.customerStories[
                'Process return requests and generate return labels'
              ]
            }
          </li>
          <li>
            {t.customerStories['Guide merchants through integration setup']}
          </li>
          <li>
            {
              t.customerStories[
                'Answer billing questions with accurate invoice data'
              ]
            }
          </li>
        </ul>

        <blockquote className="not-prose my-10 rounded-2xl border border-neutral-200 bg-[#fafaf9] p-7">
          <p className="text-lg leading-snug text-neutral-800">
            “
            {
              t.customerStories[
                'We wanted AI to handle the routine so our team could focus on helping merchants grow their businesses. Not the other way around.'
              ]
            }
            ”
          </p>
          <p className="mt-4 text-sm font-medium text-neutral-600">
            {t.customerStories['Mohamed Razaz, CEO at OTO']}
          </p>
        </blockquote>

        <h2>{t.customerStories['The Results']}</h2>
        <p>{t.customerStories['After implementing Open, OTO achieved:']}</p>

        <CaseStudyResultsTable
          metricLabel={t.customerStories['Metric']}
          impactLabel={t.customerStories['Result']}
          rows={[
            {
              metric: t.customerStories['Automation rate'],
              impact: t.customerStories['77% of inquiries handled by AI'],
            },
            {
              metric: t.customerStories['Customer satisfaction'],
              impact: t.customerStories['90%+ CSAT maintained'],
            },
            {
              metric: t.customerStories['Response time'],
              impact: t.customerStories['Instant for automated queries'],
            },
            {
              metric: t.customerStories['Team focus'],
              impact: t.customerStories['Complex merchant issues only'],
            },
          ]}
        />

        <p>
          {
            t.customerStories[
              'The 90%+ CSAT score is significant. It shows that merchants are happy with AI-powered support when it actually solves their problems instead of deflecting them to a queue.'
            ]
          }
        </p>

        <h2>{t.customerStories['What Made It Work']}</h2>
        <p>
          {
            t.customerStories[
              "Three things made OTO's AI implementation successful:"
            ]
          }
        </p>
        <p>
          <strong>{t.customerStories['1. Deep integration.']}</strong>{' '}
          {
            t.customerStories[
              "The AI connects to OTO's shipping data, carrier APIs, and billing systems. It doesn't just answer questions - it takes actions and provides real information."
            ]
          }
        </p>
        <p>
          <strong>{t.customerStories['2. Clear escalation paths.']}</strong>{' '}
          {
            t.customerStories[
              'Complex issues like carrier disputes or custom integrations go straight to humans. The AI knows its limits.'
            ]
          }
        </p>
        <p>
          <strong>{t.customerStories['3. Continuous training.']}</strong>{' '}
          {
            t.customerStories[
              "OTO's team regularly reviews AI conversations and updates the knowledge base. The system gets smarter over time."
            ]
          }
        </p>

        <h2>{t.customerStories['Looking Ahead']}</h2>
        <p>
          {
            t.customerStories[
              'OTO is expanding to new markets and adding more carriers. With AI handling routine support, they can scale without proportionally growing the support team. The human agents focus on what they do best: helping merchants solve complex logistics challenges and grow their businesses.'
            ]
          }
        </p>
      </CaseStudyBody>

      <CustomerVideos />

      <Testimonials />
    </>
  );
};

export default OTOCaseStudy;
